import cn from 'classnames';
import PropTypes from 'prop-types';
import { useMemo, useState, Fragment } from 'react';

import SimpleCheckbox from '../common/SimpleCheckbox';
import { SearchInput } from '../common/Search';

import styles from './Filters.module.scss';

const SELECT_ALL_VALUE = 'select-all-value';

export function MultipleFilter(props) {
  const {
    name,
    options,
    onChange,
    onReset,
    disabled = false,
    withButtons = false,
    allowEmpty = true,
    withSelectAll = false,
    minWidth = 150,
    className = '',
  } = props;

  const [current, setCurrent] = useState(props.selected);
  const [search, setSearch] = useState('');

  const optionsWithoutSeparators = options.filter((option) => !option.separator);
  const allSelected = current.length === optionsWithoutSeparators.filter((option) => !option.disabled).length;

  const onOptionChange = (value, checked) => {
    let newSelected;
    if (value === SELECT_ALL_VALUE) {
      newSelected = allSelected
        ? []
        : optionsWithoutSeparators.filter((option) => !option.disabled).map((option) => option.value);
    } else {
      newSelected = checked ? [...current, value] : current.filter((selectedValue) => selectedValue !== value);
      if (!allowEmpty && !withButtons && newSelected.length === 0) return;
    }

    setCurrent(newSelected);
    if (!withButtons) onChange(newSelected);
  };

  const renderOptions = () => {
    const optionsToRender = options.filter((option) =>
      option?.label ? option.label.toLowerCase().includes(search.toLowerCase()) : true,
    );
    if (withSelectAll) {
      optionsToRender.unshift(
        {
          value: SELECT_ALL_VALUE,
          label: 'Select all',
          checked: allSelected,
        },
        {
          separator: true,
        },
      );
    }

    return optionsToRender.map((option, index) => {
      if (option.separator) {
        return renderOptionWithSeparator(option, index);
      }
      const checked = option.checked || current.includes(option.value) || current.includes(Number(option.value));
      return (
        <SimpleCheckbox
          key={option.value}
          value={option.value}
          label={option.label}
          checked={checked}
          onChange={(checked) => onOptionChange(option.value, checked)}
          disabled={option.disabled || disabled}
        />
      );
    });
  };

  const renderOptionWithSeparator = (option, index) => {
    const shouldRenderSeparatorHRElement = !withSelectAll || index !== 2;

    if (option.separator && option.separatorLabel) {
      return (
        <Fragment key={`option-with-separator-${index}`}>
          {shouldRenderSeparatorHRElement && (
            <hr className={styles.separator} key={`separator-${option.separatorLabel}`} />
          )}
          <p className={styles.separatorLabel}>{option.separatorLabel}</p>
        </Fragment>
      );
    }

    if (option.separator && shouldRenderSeparatorHRElement) {
      return <hr className={styles.separator} key={`separator-${index}`} />;
    }
  };

  const isButtonDisabled = useMemo(() => !allowEmpty && current.length === 0, [current, allowEmpty]);

  return (
    <div className={cn(styles.filter, styles.multipleFilter, className)} style={{ minWidth }}>
      {name && <h5 className={styles.title}>{name}</h5>}

      {props.withSearch && <SearchInput value={search} setValue={setSearch} />}

      <div className={styles.options}>{renderOptions()}</div>
      {withButtons && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.button}
            onClick={() => {
              setCurrent(props.selected);
              onReset?.();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className={cn(styles.button, styles.commitButton)}
            onClick={() => onChange(current)}
            disabled={isButtonDisabled}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}

MultipleFilter.propTypes = {
  name: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  disabled: PropTypes.bool,
  onReset: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        separator: PropTypes.bool.isRequired,
      }),
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
      }),
    ]),
  ).isRequired,
  withButtons: PropTypes.bool,
  withSelectAll: PropTypes.bool,
  withSearch: PropTypes.bool,
  allowEmpty: PropTypes.bool,
  minWidth: PropTypes.number,
  className: PropTypes.string,
};
